import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, ArrowRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useAddStore } from '@/hooks/useAddStore';
import { GooglePlacesAutocompleteInput } from '@/pages/onboarding/components/GooglePlacesAutocompleteInput';
import { getGooglePlaceDetails, parseAddressComponents } from '@/pages/onboarding/utils';

// NEA-5838 — self-service expansion. The shop's plan, billing and contact carry
// over server-side (inherited from the current shop), so the retailer only picks
// the new store's location. On submit we create the store via the add-store
// endpoint and hand straight off to the new store's own GBP + POS onboarding.
export function AddStore() {
	const navigate = useNavigate();
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const { addStore, loading, error } = useAddStore();

	const [selectedPlace, setSelectedPlace] = useState(null);
	const [addressDetails, setAddressDetails] = useState(null);
	const [placeError, setPlaceError] = useState(null);

	const handlePlaceSelect = async (place) => {
		setSelectedPlace(place);
		setPlaceError(null);
		if (!place) {
			setAddressDetails(null);
			return;
		}
		try {
			const placeId = place.value.place_id;
			const details = await getGooglePlaceDetails(placeId);
			setAddressDetails(parseAddressComponents(details, placeId));
		} catch (err) {
			setAddressDetails(null);
			setPlaceError('Could not load that location. Please try again.');
		}
	};

	const handleSubmit = async () => {
		if (!shop || !addressDetails) return;
		const newShop = await addStore(shop.id, {
			name: addressDetails.name,
			addressLine1: addressDetails.addressLine1,
			addressLine2: addressDetails.addressLine2 || undefined,
			city: addressDetails.city || undefined,
			postcode: addressDetails.postcode,
			country: addressDetails.country,
			publicPhoneNumber: addressDetails.publicPhoneNumber || undefined,
			googlePlaceId: addressDetails.googlePlaceId,
			geolocation: addressDetails.geolocation,
			openingHours: addressDetails.openingHours || undefined
		});
		// Business details are already set on the new store, so jump straight to
		// its Google Business Profile step (step 2) of the standard onboarding.
		navigate(`/${newShop.id}/onboarding?step=2`);
	};

	return (
		<div className="mx-auto max-w-xl px-4 py-10">
			<div className="mb-6 flex items-start gap-3">
				<span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600/10 text-blue-600">
					<Store className="h-5 w-5" strokeWidth={2} />
				</span>
				<div>
					<h1 className="text-xl font-semibold text-foreground">Add another store</h1>
					<p className="text-sm text-muted-foreground">
						Your plan, billing and contact details carry over from {shop?.name || 'your store'} — just tell us where the new
						store is.
					</p>
				</div>
			</div>

			<div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
				<label htmlFor="places-autocomplete" className="mb-2 block text-sm font-medium text-foreground">
					Find your new store
				</label>
				<GooglePlacesAutocompleteInput onPlaceSelect={handlePlaceSelect} selectedPlace={selectedPlace} />
				{placeError ? <p className="mt-2 text-sm text-destructive">{placeError}</p> : null}

				{addressDetails ? (
					<div className="mt-4 rounded-xl bg-muted/50 p-3 text-sm">
						<div className="font-medium text-foreground">{addressDetails.name}</div>
						<div className="text-muted-foreground">
							{[addressDetails.addressLine1, addressDetails.city, addressDetails.postcode].filter(Boolean).join(', ')}
						</div>
					</div>
				) : null}

				{error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

				<Button className="mt-5 w-full" disabled={!addressDetails || loading} onClick={handleSubmit}>
					{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
					{loading ? 'Adding store…' : 'Add store'}
					{!loading ? <ArrowRight className="ml-2 h-4 w-4" /> : null}
				</Button>
			</div>

			<button
				type="button"
				onClick={() => navigate(shopPath('/'))}
				className="mt-4 text-sm text-muted-foreground transition-colors hover:text-foreground"
			>
				Cancel
			</button>

			{/* PlacesService target for getGooglePlaceDetails (same as the onboarding page). */}
			<div id="google-places-autocomplete-helper" />
		</div>
	);
}
